import { Button, Modal, Alert, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';

import useUserApi from '@/shared/services/userApi';
import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';

import './ProfilePopUp.css';

const ProfilePopUp = ({
    show,
    handleClose,
    currentProfilePicture = null,
    onProfileUpdate = null,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);
    const { uploadProfilePicture, deleteProfilePicture } = useUserApi();

    // process selected file
    const processSelectedFile = (file) => {
        setError(null);
        const validationError = validateImageFile(file);
        if (validationError) {
            setError(validationError);
            setSelectedFile(null);
            setPreviewUrl(currentProfilePicture || null);
            return;
        }
        // Revoke previous object URL to prevent memory leak
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    // handle file selection from input
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            processSelectedFile(file);
        }
    };

    // drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processSelectedFile(files[0]);
        }
    };

    // handle upload
    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file to upload.');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            const formData = new FormData();
            formData.append('profilePicture', selectedFile);

            const response = await uploadProfilePicture(formData);

            if (onProfileUpdate && response.data?.user) {
                onProfileUpdate(response.data.user);
            }

            // reset and close
            setSelectedFile(null);
            setPreviewUrl(null);
            handleClose();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'Failed to upload profile picture.'
            );
        } finally {
            setUploading(false);
        }
    };

    /**
     * handle delete -- uses confirm state instead of window.confirm
     */
    const handleDeleteClick = () => {
        if (!currentProfilePicture) {
            setError('No profile picture to delete.');
            return;
        }
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirmed = async () => {
        setShowDeleteConfirm(false);
        try {
            setDeleting(true);
            setError(null);

            const response = await deleteProfilePicture();

            if (onProfileUpdate && response.data?.user) {
                onProfileUpdate(response.data.user);
            }

            // reset and close
            setPreviewUrl(null);
            setSelectedFile(null);
            handleClose();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'Failed to delete profile picture.'
            );
        } finally {
            setDeleting(false);
        }
    };

    /**
     * trigger file input click
     */
    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    /**
     * reset on modal close
     */
    const handleModalClose = () => {
        if (!uploading && !deleting) {
            // Revoke object URL to prevent memory leak
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
            setSelectedFile(null);
            setPreviewUrl(null);
            setError(null);
            setIsDragging(false);
            handleClose();
        }
    };

    const displayImage =
        previewUrl || currentProfilePicture || sustainabilityHubLogo;

    return (
        <>
            <Modal
                show={show}
                onHide={handleModalClose}
                backdrop={uploading || deleting ? 'static' : true}
                keyboard={!uploading && !deleting}
                className="mt-5"
                aria-labelledby="profile-picture-modal-title"
            >
                <Modal.Header closeButton={!uploading && !deleting}>
                    <Modal.Title id="profile-picture-modal-title">
                        Update Profile Picture
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* drag and drop area */}
                    <div
                        className={`profile-picture-upload-area ${
                            isDragging ? 'dragging' : ''
                        } ${selectedFile ? 'has-file' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={handleChooseFile}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleChooseFile();
                            }
                        }}
                        aria-label="Upload profile picture area"
                    >
                        {/* profile picture preview */}
                        <img
                            className="modal-profile-pic"
                            src={displayImage}
                            alt={
                                previewUrl
                                    ? 'Profile picture preview'
                                    : 'Default profile placeholder'
                            }
                            onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = sustainabilityHubLogo;
                            }}
                        />

                        {/* upload instructions */}
                        {!selectedFile && (
                            <div className="upload-instructions">
                                <p className="mb-1">
                                    <strong>Click to upload</strong> or drag and
                                    drop
                                </p>
                                <p className="text-muted small mb-0">
                                    JPEG, PNG, or HEIC (max 5MB)
                                </p>
                            </div>
                        )}

                        {/* selected file info */}
                        {selectedFile && (
                            <div className="selected-file-info">
                                <p className="mb-1">
                                    <strong>Selected:</strong>{' '}
                                    {selectedFile.name}
                                </p>
                                <p className="text-muted small mb-0">
                                    Size:{' '}
                                    {(selectedFile.size / 1024 / 1024).toFixed(
                                        2
                                    )}{' '}
                                    MB
                                </p>
                            </div>
                        )}
                    </div>

                    {/* hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_FILE_EXTENSIONS}
                        onChange={handleFileSelect}
                        className="d-none"
                        aria-label="File input for profile picture"
                    />
                </Modal.Body>

                <Modal.Footer>
                    <div className="button-div w-100 d-flex justify-content-between">
                        <Button
                            variant="danger"
                            onClick={handleDeleteClick}
                            disabled={
                                !currentProfilePicture || uploading || deleting
                            }
                            className="modal-delete-button"
                        >
                            {deleting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>

                        <Button
                            variant="primary"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading || deleting}
                            className="modal-button"
                        >
                            {uploading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Uploading...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Confirm Delete Profile Picture Modal (replaces window.confirm) */}
            <Modal
                show={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                centered
                aria-labelledby="confirm-delete-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="confirm-delete-modal-title">
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete your profile picture?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirmed}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

ProfilePopUp.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    currentProfilePicture: PropTypes.string,
    onProfileUpdate: PropTypes.func,
};

export default ProfilePopUp;
